import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getLevelSeparatedCompetencies } from 'lib/competencies';
import {
  EmptyIndicator,
  FlexContainer,
  MoreOptions,
  Pagination,
  Search
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { DropdownSelectable } from 'components/common/dropdown';
import {
  CompetenciesProductBadge,
  CompetencyProfilesAddOrUpdate,
  CompetencyProfilesFindMatchingUsers,
  CompetencyProfilesListItem,
  CompetencyProfilesRemove
} from '.';

import type {
  GetCompetenciesRes,
  GetCompetencyProfilesRes
} from 'shared-types/responses';

const CompetencyProfiles = () => {
  const { messages } = useReduxState();

  const router = useRouter();
  const page = router.query.page ? Number(router.query.page) : 1;
  const category = (router.query.category as string) || 'all';
  const search = (router.query.search as string) || '';

  const handleCategoryChange = (value: string) => {
    const newQuery: Record<string, string> = { ...router.query, page: '1' };
    if (value !== 'all') newQuery.category = value;
    else delete newQuery.category;

    router.replace({ query: newQuery }, undefined, { shallow: true });
  };

  const { data, loading, updateData } = useApiData<GetCompetencyProfilesRes>({
    onDataUpdated: (updatedData) => {
      // removing the last profile in a category deletes the category itself,
      // so drop a filter that can no longer match anything rather than leave
      // the dropdown showing a category that is gone
      if (category !== 'all' && !updatedData.categories?.includes(category)) {
        handleCategoryChange('all');
      }
    },
    url: `/admin/competency-profiles?category=${encodeURIComponent(
      category
    )}&search=${search}&page=${page}`
  });

  const { data: optionsData } = useApiData<GetCompetenciesRes>({
    url: `/admin/competencies?limit=-1`
  });

  const competencyOptions = getLevelSeparatedCompetencies(
    optionsData?.competencyObjs || []
  );

  const handlePageChange = ({ page }: { page: number }) => {
    router.replace({ query: { ...router.query, page } }, undefined, {
      shallow: true
    });
  };

  return (
    <>
      <Box
        headerContent={
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10
            }}
          >
            <DropdownSelectable
              category="lighter"
              defaultValue="all"
              onChange={handleCategoryChange}
              options={[
                { text: messages.commonAll, value: 'all' },
                ...(data?.categories?.map((category) => ({
                  text: category,
                  value: category
                })) || [])
              ]}
              selectedValueBefore={`${messages.commonCategory}:`}
              showSelectedText={true}
              size="header"
              value={category}
            />

            <div style={{ minWidth: 200 }}>
              <Search
                placeholder={messages.commonSearch}
                withUrl={true}
                size="header"
              />
            </div>

            <CompetencyProfilesAddOrUpdate
              categoryOptions={data?.categories || []}
              competencyOptions={competencyOptions}
              onSubmit={updateData}
            />
          </div>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.competencyProfiles}
        titleDescription={messages.commonManagedBySuperadminsOnly}
      >
        {data?.competencyProfiles?.length ? (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
                <BoxTableCell>{messages.competencyProfile}</BoxTableCell>
                <BoxTableCell>{messages.analyticsCompetencies}</BoxTableCell>
                <BoxTableCell>{messages.commonCategories}</BoxTableCell>
                <BoxTableCell shrink={true} />
              </BoxTableHeader>

              {data.competencyProfiles.map((profile) => (
                <BoxTableRow key={profile.id}>
                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    verticalAlign="top"
                  >
                    <CompetencyProfilesListItem competencyProfile={profile} />
                  </BoxTableCell>

                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    verticalAlign="top"
                  >
                    <FlexContainer
                      style={{ flexWrap: 'wrap', gap: 10, padding: '5px 0' }}
                    >
                      {profile.competencies.map((competency) => (
                        <CompetenciesProductBadge
                          key={`${competency.id}-${competency.level?.id || 0}`}
                          competency={competency}
                        />
                      ))}
                    </FlexContainer>
                  </BoxTableCell>

                  <BoxTableCell
                    cellBreakpoint={breakpoints.medium}
                    label={messages.commonCategories}
                    verticalAlign="top"
                  >
                    {profile.categories?.length ? (
                      <div style={{ paddingTop: 5 }}>
                        {profile.categories.join(', ')}
                      </div>
                    ) : null}
                  </BoxTableCell>

                  <BoxTableCell shrink={true} verticalAlign="top">
                    <MoreOptions
                      renderAsFixed={true}
                      styleContentButtons={false}
                    >
                      <CompetencyProfilesAddOrUpdate
                        categoryOptions={data?.categories || []}
                        competencyOptions={competencyOptions}
                        competencyProfile={profile}
                        onSubmit={updateData}
                      />

                      <CompetencyProfilesFindMatchingUsers
                        competencyProfile={profile}
                      />

                      <CompetencyProfilesRemove
                        competencyProfileId={profile.id}
                        onRemove={updateData}
                      />
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : (
          <EmptyIndicator
            icon="address-card"
            title={messages.competencyProfilesEmpty}
          />
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default CompetencyProfiles;
